const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TOKEN_KEY   = 'gg_access_token';
const REFRESH_KEY = 'gg_refresh_token';
const USER_KEY    = 'gg_user';

let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

const apiFetch = async (url, options = {}, returnRaw = false) => {
  const token = localStorage.getItem(TOKEN_KEY);

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let res = await fetch(`${API_URL}${url}`, { ...options, headers });

  if (res.status === 401) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        headers['Authorization'] = `Bearer ${newToken}`;
        return fetch(`${API_URL}${url}`, { ...options, headers });
      });
    }

    isRefreshing = true;
    const refreshToken = localStorage.getItem(REFRESH_KEY);

    if (!refreshToken) {
      clearAuth();
      window.location.href = '/';
      return res;
    }

    try {
      const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (!refreshRes.ok) throw new Error('Refresh failed');

      const data = await refreshRes.json();
      localStorage.setItem(TOKEN_KEY,   data.accessToken);
      localStorage.setItem(REFRESH_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY,    JSON.stringify(data.user));

      processQueue(null, data.accessToken);
      headers['Authorization'] = `Bearer ${data.accessToken}`;
      res = await fetch(`${API_URL}${url}`, { ...options, headers });
    } catch (err) {
      processQueue(err, null);
      clearAuth();
      window.location.href = '/';
    } finally {
      isRefreshing = false;
    }
  }

  if (returnRaw) return res;

  if (res.ok) {
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }

  return res;
};

// Multipart/form-data upload — 401'de token refresh dener
const apiUpload = async (url, formData) => {
  const doRequest = (token) => {
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    return fetch(`${API_URL}${url}`, { method: 'POST', headers, body: formData });
  };

  let token = localStorage.getItem(TOKEN_KEY);
  let res = await doRequest(token);

  // Token expire olduysa refresh dene
  if (res.status === 401) {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) { clearAuth(); window.location.href = '/'; return; }

    try {
      const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      if (!refreshRes.ok) throw new Error('Refresh failed');

      const data = await refreshRes.json();
      localStorage.setItem(TOKEN_KEY,   data.accessToken);
      localStorage.setItem(REFRESH_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY,    JSON.stringify(data.user));

      res = await doRequest(data.accessToken); // tekrar dene
    } catch {
      clearAuth();
      window.location.href = '/';
      return;
    }
  }

  if (!res.ok) {
    const text = await res.text();
    let msg = `Upload hatası (${res.status})`;
    try { msg = JSON.parse(text)?.error || msg; } catch { }
    throw new Error(msg);
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

// ── API METODLARI ──

export const api = {
  // ── Venues ──
  getVenues:   ()         => apiFetch('/api/venues'),
  getVenue:    (id)       => apiFetch(`/api/venues/${id}`),
  createVenue: (data)     => apiFetch('/api/venues',       { method: 'POST', body: JSON.stringify(data) }),
  updateVenue: (id, data) => apiFetch(`/api/venues/${id}`, { method: 'PUT',  body: JSON.stringify(data) }),
  deleteVenue: (id)       => apiFetch(`/api/venues/${id}`, { method: 'DELETE' }),

  // Salon fotoğraf — FormData'yı burada oluşturuyoruz
  uploadVenuePhoto: (venueId, file, isMainPhoto) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiUpload(`/api/venues/${venueId}/upload-photo?isMainPhoto=${isMainPhoto}`, formData);
  },

  deleteVenuePhoto: (venueId, photoUrl) =>
    apiFetch(`/api/venues/${venueId}/photos?photoUrl=${encodeURIComponent(photoUrl)}`, { method: 'DELETE' }),

  // ── Events ──
  getEvents:      ()         => apiFetch('/api/events'),
  getEvent:       (id)       => apiFetch(`/api/events/${id}`),
  createEvent:    (data)     => apiFetch('/api/events',        { method: 'POST', body: JSON.stringify(data) }),
  updateEvent:    (id, data) => apiFetch(`/api/events/${id}`,  { method: 'PUT',  body: JSON.stringify(data) }),
  deleteEvent:    (id)       => apiFetch(`/api/events/${id}`,  { method: 'DELETE' }),
  calculatePrice: (data)     => apiFetch('/api/calculate-price', { method: 'POST', body: JSON.stringify(data) }),

  // Etkinlik fotoğraf
  uploadEventPhoto: (eventId, file, isMainPhoto) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiUpload(`/api/events/${eventId}/upload-photo?isMainPhoto=${isMainPhoto}`, formData);
  },

  deleteEventPhoto: (eventId, photoUrl) =>
    apiFetch(`/api/events/${eventId}/photos?photoUrl=${encodeURIComponent(photoUrl)}`, { method: 'DELETE' }),

  // Bloklu koltuklar
  getBlockedSeats:    (eventId)        => apiFetch(`/api/events/${eventId}/blocked-seats`),
  updateBlockedSeats: (eventId, seats) => apiFetch(`/api/events/${eventId}/blocked-seats`, { method: 'PUT', body: JSON.stringify(seats) }),

  // ── Reservations ──
  getReservations:      ()         => apiFetch('/api/reservations'),
  getReservation:       (id)       => apiFetch(`/api/reservations/${id}`),
  getEventReservations: (eventId)  => apiFetch(`/api/reservations/event/${eventId}`),
  getReservedSeats:     (eventId)  => apiFetch(`/api/reservations/event/${eventId}/seats`),
  createReservation:    (data)     => apiFetch('/api/reservations',        { method: 'POST', body: JSON.stringify(data) }),
  updateReservation:    (id, data) => apiFetch(`/api/reservations/${id}`,  { method: 'PUT',  body: JSON.stringify(data) }),
  deleteReservation:    (id)       => apiFetch(`/api/reservations/${id}`,  { method: 'DELETE' }),
  updatePayment:        (id, data) => apiFetch(`/api/reservations/${id}/payment`, { method: 'PUT', body: JSON.stringify(data) }),
  cancelReservation:    (id, cancelledBy) => apiFetch(`/api/reservations/${id}/cancel`, { method: 'PUT', body: JSON.stringify({ cancelledBy }) }),

  // ── Invitation Codes ──
  getInvitationCodes:     (eventId) => apiFetch(`/api/events/${eventId}/invitation-codes`),
  createInvitationCode:   (data)    => apiFetch('/api/invitation-codes',         { method: 'POST', body: JSON.stringify(data) }),
  bulkCreateCodes:        (data)    => apiFetch('/api/invitation-codes/bulk',    { method: 'POST', body: JSON.stringify(data) }),
  updateInvitationCode:   (id, data)=> apiFetch(`/api/invitation-codes/${id}`,   { method: 'PUT',  body: JSON.stringify(data) }),
  deleteInvitationCode:   (id)      => apiFetch(`/api/invitation-codes/${id}`,   { method: 'DELETE' }),
  validateInvitationCode: (data)    => apiFetch('/api/invitation-codes/validate', { method: 'POST', body: JSON.stringify(data) }),
};